import React, {useState, useEffect, useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Button, DateInput, SelectInput, Text, TextInput} from '@/ui-kits';
import {useToast} from '@/hooks';
import {
  specialistAdminDashboardConsultationsSelector,
  specialistAdminDashboardCurrentPageSelector,
  specialistAdminDashboardTotalPagesSelector,
  specialistAdminDashboardTotalResultsSelector,
  specialistAdminDashboardStatus,
  specialistAdminDashboardFailureSelector,
} from '@/services/redux/selectors/specialistAdminDashboardSelector';
import {getSpecialistAdminConsultationListRequest, resetSpecialistAdminMessage} from '@/services/redux/slices/specialistAdminSlice';
import {ErrorCodes, getErrorMessage} from '@/utils/getMessages';
import {DataTable} from '@/components';
import {ConsultationStatus, ConsultationType, SpecialistConsultationStatusLabels, ConsultationTypeLabels} from '@/constants';
import {defaultDeadlineHours} from '@/constants/defaults';
import {DataTableColumn, SpecialistAdminConsultation, GetSpecialistAdminConsultationListRequestPayload, SelectInputitem} from '@/interface';
import {enumToSelectOptions, formatDate, formatName, calculateTATHours} from '@/utils/common_functions';

const StatusFilter: SelectInputitem[] = [
  {id: ConsultationStatus.AwaitingDoctorReview, label: SpecialistConsultationStatusLabels[ConsultationStatus.AwaitingDoctorReview]},
  {id: ConsultationStatus.UnderReviewByDoctor, label: SpecialistConsultationStatusLabels[ConsultationStatus.UnderReviewByDoctor]},
  {id: ConsultationStatus.ReportReady, label: SpecialistConsultationStatusLabels[ConsultationStatus.ReportReady]},
  {id: ConsultationStatus.Completed, label: SpecialistConsultationStatusLabels[ConsultationStatus.Completed]},
  {id: ConsultationStatus.CancelledByPatient, label: SpecialistConsultationStatusLabels[ConsultationStatus.CancelledByPatient]},
  {id: ConsultationStatus.Overdue, label: SpecialistConsultationStatusLabels[ConsultationStatus.Overdue]},
];

const TypeFilterOptions = enumToSelectOptions(ConsultationType);

const columns: DataTableColumn<SpecialistAdminConsultation>[] = [
  {
    name: 'id',
    align: 'center',
    render: row => row.id,
    title: <Text>CASE ID</Text>,
    width: {minWidth: '150px'},
  },
  {
    name: 'specialist id',
    align: 'center',
    render: row => row.specialist_id ?? '--',
    title: <Text>SPECIALIST ID</Text>,
    width: {minWidth: '160px'},
  },
  {
    name: 'specialist name',
    align: 'left',
    render: row => row.specialist_name ? 'Dr. ' + formatName(row.specialist_name) : '--',
    title: <Text>SPECIALIST NAME</Text>,
    width: {minWidth: '200px'},
  },
  {
    name: 'patient name',
    align: 'left',
    render: row => formatName(row.patient_name),
    title: <Text>PATIENT</Text>,
    width: {minWidth: '170px'},
  },
  {
    name: 'specialization',
    align: 'left',
    render: row => row.specialization.label,
    title: <Text>SPECIALIZATION</Text>,
    width: {minWidth: '200px'},
  },
  {
    name: 'type',
    align: 'left',
    render: row => ConsultationTypeLabels[row.consultation_type.id as ConsultationType],
    title: <Text>CONSULTATION TYPE</Text>,
    width: {minWidth: '230px'},
  },
  {
    name: 'date',
    align: 'center',
    render: row => formatDate(row.created_at, 'yyyy-MMM-dd, HH:mm:ss'),
    title: <Text>BOOKED DATE & TIME</Text>,
    width: {minWidth: '230px'},
  },
  {
    name: 'status',
    align: 'left',
    render: row => SpecialistConsultationStatusLabels[row.status.id],
    title: <Text>STATUS</Text>,
    width: {minWidth: '100px'},
  },
  {
    name: 'turnaround time',
    align: 'center',
    render: row => {
      if (row.status.id === ConsultationStatus.ReportReady || row.status.id === ConsultationStatus.Completed) {
        if (!row.close_at) return '--';
        const tat = calculateTATHours(row.created_at, row.close_at);
        const hours = Math.floor((new Date(row.close_at).getTime() - new Date(row.created_at).getTime()) / (1000 * 60 * 60));
        const isOverDeadline = hours > defaultDeadlineHours;
        return (
          <span>
            {formatDate(row.close_at, 'dd-MMM-yyyy, HH:mm:ss')}{' '}
            <span className={isOverDeadline ? 'text-ui_red' : ''}>({tat})</span>
          </span>
        );
      }
      return '--';
    },
    title: <Text>TURNAROUND TIME (TAT)</Text>,
    width: {minWidth: '250px'},
  },
];

export const SpecialistAdminDashboard: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const {showToast} = useToast();
  const dispatch = useDispatch();

  // ==========================
  // selectors
  // ==========================
  const consultations = useSelector(specialistAdminDashboardConsultationsSelector);
  const consultationsCurrentPage = useSelector(specialistAdminDashboardCurrentPageSelector);
  const consultationsTotalPages = useSelector(specialistAdminDashboardTotalPagesSelector);
  const consultationsTotalResults = useSelector(specialistAdminDashboardTotalResultsSelector);
  const consultationsFailure = useSelector(specialistAdminDashboardFailureSelector) as ErrorCodes;
  const isLoading = useSelector(specialistAdminDashboardStatus);

  // ===========================
  // states
  // ===========================
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<ConsultationStatus | null>(null);
  const [type, setType] = useState<ConsultationType | null>(null);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // ===========================
  // functions
  // ===========================
  const resetFilter = () => {
    setPage(1);
    setSearch('');
    setStatus(null);
    setType(null);
    setStartDate(null);
    setEndDate(null);
  };

  const fetchConsultations = useCallback(() => {
    let isFilterActive = false;
    const payload: GetSpecialistAdminConsultationListRequestPayload = {
      page,
    };
    if (search) {
      payload.search_text = search;
      isFilterActive = true;
    }
    if (status) {
      payload.status = status;
      isFilterActive = true;
    }
    if (type) {
      payload.consultation_type = type;
      isFilterActive = true;
    }
    if (startDate) {
      payload.start_date = startDate.toISOString();
      isFilterActive = true;
    }
    if (endDate) {
      payload.end_date = endDate.toISOString();
      isFilterActive = true;
    }
    setIsFilterActive(isFilterActive);
    dispatch(getSpecialistAdminConsultationListRequest(payload));
  }, [page, search, status, type, startDate, endDate]);

  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    const timeout = setTimeout(fetchConsultations, 500);
    return () => clearTimeout(timeout);
  }, [fetchConsultations]);

  useEffect(() => {
    if (consultationsFailure) {
      if (['E-10044', 'E-10054'].includes(consultationsFailure)) {
        setErrorMessage(getErrorMessage(consultationsFailure));
        return;
      }
      else{
        showToast(getErrorMessage(consultationsFailure), 'failure');
      }
    }
    return () => {
      dispatch(resetSpecialistAdminMessage());
    };
  }, [consultationsFailure]);

  return (
    <div className="p-2 sm:py-4: md:p-6 flex flex-col grow gap-4">
      <Text variant="heading" className="text-ui_blue mb-4">
        Specialist Consultation
      </Text>

      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={search}
          onChangeText={setSearch}
          leftIcon="search_blue"
          className="sm:max-w-[300px] w-full col-span-full"
          wrapperClassName="px-2 border"
          placeholder="Search by Name, ID & case ID"
        />
        <SelectInput
          value={status}
          onChange={value => setStatus(value.id)}
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
          placeholder="Status"
          wrapperClassName="border"
          options={StatusFilter}
        />
        <SelectInput
          value={type}
          onChange={value => setType(value.id)}
          className="sm:max-w-[200px] w-full"
          placeholder="Consultation Type"
          wrapperClassName="border"
          itemTextClassName="!text-sm"
          options={TypeFilterOptions}
        />

        <DateInput
          value={startDate}
          onChangeDate={setStartDate}
          placeholder="From"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          textClassName="text-start"
          maxValue={endDate ?? new Date()}
        />
        <DateInput
          value={endDate}
          onChangeDate={setEndDate}
          placeholder="To"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          textClassName="text-start"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          minValue={startDate ?? undefined}
          maxValue={new Date()}
        />

        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={consultations}
        totalPages={consultationsTotalPages}
        currentPage={consultationsCurrentPage}
        totalResult={consultationsTotalResults}
        onPageChange={setPage}
        loading={isLoading}
        pageSize={50}
        errorMessage={errorMessage}
      />
    </div>
  );
};
